import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  alpha,
  LinearProgress,
  InputAdornment,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, writeBatch } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const ChainManager = () => {
  const { user } = useAuth();
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editChain, setEditChain] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logoUrl: '',
    displayOrder: 0,
    primaryColor: '#1A73E8', // Default blue color
    isHQ: false // Added isHQ field
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Load chains from Firestore
  useEffect(() => {
    const fetchChains = async () => {
      try {
        setLoading(true);
        // Query chains ordered by displayOrder, then by name
        const chainsQuery = query(
          collection(db, 'chains'), 
          orderBy('displayOrder', 'asc'), 
          orderBy('name', 'asc')
        );
        const querySnapshot = await getDocs(chainsQuery);
        
        const chainsList = [];
        querySnapshot.forEach((doc) => {
          chainsList.push({
            id: doc.id,
            displayOrder: doc.data().displayOrder || 0,
            ...doc.data()
          });
        });
        
        setChains(chainsList);
      } catch (error) {
        console.error('Error fetching chains:', error);
        setSnackbar({
          open: true,
          message: `Error loading chains: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchChains();
  }, []);

  const handleOpenDialog = (chain = null) => {
    if (chain) {
      setEditChain(chain);
      setFormData({
        name: chain.name || '',
        description: chain.description || '',
        logoUrl: chain.logoUrl || '',
        displayOrder: chain.displayOrder || 0,
        primaryColor: chain.primaryColor || '#1A73E8', // Use existing color or default
        isHQ: chain.isHQ || false // Set isHQ value from existing chain
      });
    } else {
      // For a new chain, set displayOrder to the highest value + 1
      const highestOrder = chains.length > 0 
        ? Math.max(...chains.map(c => c.displayOrder || 0))
        : -1;
      
      setEditChain(null);
      setFormData({
        name: '',
        description: '',
        logoUrl: '',
        displayOrder: highestOrder + 1,
        primaryColor: '#1A73E8', // Default blue color
        isHQ: false // Default isHQ to false
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditChain(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file is an image
    if (!file.type.match('image.*')) {
      setSnackbar({
        open: true,
        message: 'Please select an image file (PNG, JPG, etc.)',
        severity: 'error'
      });
      return;
    }

    // Validate file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'Image size should be less than 2MB',
        severity: 'error'
      });
      return;
    }

    uploadImage(file);
  };

  const uploadImage = (file) => {
    setIsUploading(true);
    setUploadProgress(0);

    // Create a unique filename
    const fileName = `chain_logos/${Date.now()}_${file.name.replace(/\s+/g, '_')}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    // Track upload progress
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        // Handle errors
        console.error('Upload error:', error);
        setIsUploading(false);
        setSnackbar({
          open: true,
          message: `Upload failed: ${error.message}`,
          severity: 'error'
        });
      },
      () => {
        // Upload complete
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFormData(prev => ({
            ...prev,
            logoUrl: downloadURL
          }));
          setIsUploading(false);
          setSnackbar({
            open: true,
            message: 'Image uploaded successfully',
            severity: 'success'
          });
        });
      }
    );
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      if (!formData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'Chain name is required',
          severity: 'error'
        });
        setIsSaving(false);
        return;
      }

      if (editChain) {
        // Update existing chain
        await updateDoc(doc(db, 'chains', editChain.id), {
          name: formData.name.trim(),
          description: formData.description.trim(),
          logoUrl: formData.logoUrl.trim(),
          displayOrder: Number(formData.displayOrder) || 0,
          primaryColor: formData.primaryColor || '#1A73E8', // Include primary color in update
          isHQ: formData.isHQ, // Include isHQ flag in update
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });
        
        setSnackbar({
          open: true,
          message: 'Chain updated successfully',
          severity: 'success'
        });
      } else {
        // Add new chain
        await addDoc(collection(db, 'chains'), {
          name: formData.name.trim(),
          description: formData.description.trim(),
          logoUrl: formData.logoUrl.trim(),
          displayOrder: Number(formData.displayOrder) || 0,
          primaryColor: formData.primaryColor || '#1A73E8', // Include primary color in new chain
          isHQ: formData.isHQ, // Include isHQ flag in new chain
          createdAt: serverTimestamp(),
          createdBy: user.email,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });
        
        setSnackbar({
          open: true,
          message: 'Chain added successfully',
          severity: 'success'
        });
      }

      // Refresh the chains list
      const chainsQuery = query(
        collection(db, 'chains'), 
        orderBy('displayOrder', 'asc'), 
        orderBy('name', 'asc')
      );
      const querySnapshot = await getDocs(chainsQuery);
      
      const updatedChains = [];
      querySnapshot.forEach((doc) => {
        updatedChains.push({
          id: doc.id,
          displayOrder: doc.data().displayOrder || 0,
          ...doc.data()
        });
      });
      
      setChains(updatedChains);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving chain:', error);
      setSnackbar({
        open: true,
        message: `Error saving chain: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteChain = async (chainId) => {
    if (window.confirm('Are you sure you want to delete this chain?')) {
      try {
        setIsSaving(true);
        await deleteDoc(doc(db, 'chains', chainId));
        
        // Update local state
        setChains(chains.filter(chain => chain.id !== chainId));
        
        setSnackbar({
          open: true,
          message: 'Chain deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting chain:', error);
        setSnackbar({
          open: true,
          message: `Error deleting chain: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleMoveChain = async (chainId, direction) => {
    try {
      setIsSaving(true);
      const chainIndex = chains.findIndex(chain => chain.id === chainId);
      if (chainIndex === -1) return;
      
      // Cannot move up if already at the top
      if (direction === 'up' && chainIndex === 0) return;
      
      // Cannot move down if already at the bottom
      if (direction === 'down' && chainIndex === chains.length - 1) return;
      
      const newChains = [...chains];
      const chainToMove = newChains[chainIndex];
      
      // Determine the swap index
      const swapIndex = direction === 'up' ? chainIndex - 1 : chainIndex + 1;
      const chainToSwap = newChains[swapIndex];
      
      // Swap the displayOrder values
      const tempOrder = chainToMove.displayOrder;
      chainToMove.displayOrder = chainToSwap.displayOrder;
      chainToSwap.displayOrder = tempOrder;
      
      // Reorder the array
      newChains[chainIndex] = chainToSwap;
      newChains[swapIndex] = chainToMove;
      
      // Update Firestore in a batch
      const batch = writeBatch(db);
      batch.update(doc(db, 'chains', chainToMove.id), { 
        displayOrder: chainToMove.displayOrder,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      batch.update(doc(db, 'chains', chainToSwap.id), { 
        displayOrder: chainToSwap.displayOrder,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      await batch.commit();
      
      // Update local state with sorted chains
      setChains(newChains.sort((a, b) => a.displayOrder - b.displayOrder));
      
      setSnackbar({
        open: true,
        message: 'Chain order updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error reordering chains:', error);
      setSnackbar({
        open: true,
        message: `Error reordering chains: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: 1200, 
      mx: 'auto',
      bgcolor: theme => alpha(theme.palette.background.default, 0.6),
      borderRadius: 2,
      boxShadow: '0 0 20px rgba(0,0,0,0.03)'
    }}>
      {/* Header */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          borderRadius: 2, 
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            Chain Management
            <Chip 
              size="small" 
              label={`${chains.length} chains`} 
              color="primary" 
              variant="outlined"
              sx={{ ml: 1, fontWeight: 'normal', fontSize: '0.7rem', color: 'white', borderColor: 'white' }}
            />
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading || isSaving}
            sx={{ 
              borderRadius: 1.5,
              px: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            Add Chain
          </Button>
        </Box>
        <CardContent sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Typography variant="body2" color="text.secondary">
            Manage hotel chains and their display order. Changes made here will affect how chains appear throughout the application.
            Drag chains up or down to change their order.
          </Typography>
        </CardContent>
      </Card>

      {/* Main Content */}
      <Card 
        elevation={0} 
        sx={{ 
          width: '100%', 
          overflow: 'hidden', 
          mb: 4, 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            p: 6,
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Loading chains...
            </Typography>
          </Box>
        ) : chains.length === 0 ? (
          <Box sx={{ 
            p: 6, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <Box 
              sx={{ 
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                borderRadius: '50%',
                p: 2,
                mb: 2
              }}
            >
              <InfoOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h6" color="text.primary" gutterBottom>
              No Chains Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto', mb: 3 }}>
              Click "Add Chain" to create your first chain. Chains help organize hotels and provide consistent branding.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 1.5 }}
            >
              Add Your First Chain
            </Button>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.03)
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Chain Order Preview
              </Typography>
              <Tooltip title="This shows how chains will appear in the welcome screen">
                <InfoOutlinedIcon fontSize="small" color="action" sx={{ ml: 1 }} />
              </Tooltip>
            </Box>
            
            {/* Chain preview row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                p: 2,
                gap: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.2),
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: theme => alpha(theme.palette.primary.main, 0.3),
                  }
                }
              }}
            >
              {chains.map((chain) => (
                <Tooltip key={chain.id} title={chain.name}>
                  <Box
                    sx={{
                      height: 40,
                      minWidth: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={chain.logoUrl}
                      alt={chain.name}
                      loading="lazy"
                      sx={{
                        height: '100%',
                        maxWidth: 100,
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
            
            {/* Table of chains */}
            <TableContainer sx={{ maxHeight: 'calc(100vh - 349px)' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Chain</Typography>
                    </TableCell>
                    <TableCell sx={{ width: 140 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Logo</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Description</Typography>
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Color</Typography>
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Type</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 200 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Actions</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chains.map((chain, index) => (
                    <TableRow 
                      key={chain.id} 
                      hover
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.04)
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {chain.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {chain.logoUrl ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Box
                              component="img"
                              src={chain.logoUrl}
                              alt={chain.name}
                              loading="lazy"
                              sx={{
                                height: 40,
                                maxWidth: 100,
                                objectFit: 'contain'
                              }}
                            />
                            <Tooltip title="View logo URL">
                              <IconButton 
                                size="small" 
                                onClick={() => window.open(chain.logoUrl, '_blank')}
                                sx={{ 
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'primary.main'
                                  }
                                }}
                              >
                                <LinkIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Chip 
                            label="No logo" 
                            size="small" 
                            variant="outlined" 
                            color="warning"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {chain.description || <span style={{ fontStyle: 'italic', opacity: 0.7 }}>No description</span>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {chain.primaryColor ? (
                            <>
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 1,
                                  bgcolor: chain.primaryColor,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  flexShrink: 0,
                                  boxShadow: theme => `0 2px 4px ${alpha(chain.primaryColor, 0.2)}`
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {chain.primaryColor.toUpperCase()}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Default
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {chain.isHQ ? (
                          <Chip 
                            label="HQ" 
                            size="small" 
                            color="secondary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        ) : (
                          <Chip 
                            label="Hotel" 
                            size="small" 
                            color="default"
                            variant="outlined"
                            sx={{ opacity: 0.7 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack 
                          direction="row" 
                          spacing={1} 
                          justifyContent="center"
                        >
                          <Tooltip title="Move Up">
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleMoveChain(chain.id, 'up')}
                                disabled={index === 0 || isSaving}
                                sx={{ 
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.2),
                                  },
                                  '&.Mui-disabled': {
                                    bgcolor: 'action.disabledBackground',
                                  }
                                }}
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Move Down">
                            <span>
                              <IconButton 
                                size="small"
                                onClick={() => handleMoveChain(chain.id, 'down')}
                                disabled={index === chains.length - 1 || isSaving}
                                sx={{ 
                                  bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.2),
                                  },
                                  '&.Mui-disabled': {
                                    bgcolor: 'action.disabledBackground',
                                  }
                                }}
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Edit Chain">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(chain)}
                              disabled={isSaving}
                              sx={{ 
                                bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                                color: 'info.main',
                                '&:hover': {
                                  bgcolor: theme => alpha(theme.palette.info.main, 0.2),
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete Chain">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteChain(chain.id)}
                              disabled={isSaving}
                              sx={{ 
                                bgcolor: theme => alpha(theme.palette.error.main, 0.1),
                                color: 'error.main',
                                '&:hover': {
                                  bgcolor: theme => alpha(theme.palette.error.main, 0.2),
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Card>

      {/* Add/Edit Chain Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 2, 
          bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editChain ? 'Edit Chain' : 'Add New Chain'}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                autoFocus
                name="name"
                label="Chain Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                name="description"
                label="Description"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
                placeholder="Enter a brief description of this hotel chain"
                sx={{ mb: 2 }}
              />
              <TextField
                name="logoUrl"
                label="Logo URL"
                fullWidth
                variant="outlined"
                value={formData.logoUrl}
                onChange={handleInputChange}
                helperText="Enter URL to chain logo image or use the upload button below"
                placeholder="https://example.com/logo.png"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ 
                      color: 'action.active', 
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center' 
                    }}>
                      <LinkIcon fontSize="small" />
                    </Box>
                  ),
                }}
                sx={{ mb: 1 }}
              />

              {/* File Upload Button */}
              <Box sx={{ mb: 2 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => fileInputRef.current.click()}
                  disabled={isUploading}
                  sx={{ 
                    borderRadius: 1.5,
                    textTransform: 'none',
                    mb: 1
                  }}
                >
                  {isUploading ? 'Uploading...' : 'Upload Logo Image'}
                </Button>

                {isUploading && (
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ 
                        height: 6,
                        borderRadius: 3,
                        mb: 1
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(uploadProgress)}% uploaded
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Primary Color Input */}
              <TextField
                name="primaryColor"
                label="Primary Color"
                fullWidth
                variant="outlined"
                value={formData.primaryColor}
                onChange={handleInputChange}
                placeholder="#1A73E8"
                helperText="Enter a hex color code (e.g. #1A73E8) or use the color picker"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ 
                      color: 'action.active', 
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center' 
                    }}>
                      <ColorLensIcon fontSize="small" />
                    </Box>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => handleInputChange({ target: { name: 'primaryColor', value: e.target.value } })}
                        style={{ 
                          width: '28px', 
                          height: '28px', 
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                name="displayOrder"
                label="Display Order"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.displayOrder}
                onChange={handleInputChange}
                helperText="Lower numbers will appear first in lists and selectors"
              />

              {/* HQ Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isHQ}
                      onChange={handleInputChange}
                      name="isHQ"
                      color="secondary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1">Headquarters Chain</Typography>
                      <Chip 
                        label="HQ" 
                        size="small" 
                        color="secondary"
                        sx={{ fontWeight: 'bold' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        (HQ properties will look different in the hotel list and will be excluded from hotel counts)
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                Logo Preview
              </Typography>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  height: '100%',
                  maxHeight: 250,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: theme => alpha(theme.palette.background.default, 0.5)
                }}
              >
                {formData.logoUrl ? (
                  <Box
                    component="img"
                    src={formData.logoUrl}
                    alt="Logo Preview"
                    loading="lazy"
                    sx={{
                      maxHeight: 150,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      boxShadow: `0 4px 20px ${alpha(formData.primaryColor, 0.2)}`,
                      transition: 'all 0.3s ease'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDEwIDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                      e.target.style.padding = '20px';
                      e.target.style.opacity = '0.5';
                    }}
                  />
                ) : (
                  <Box 
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      color: 'text.secondary',
                      gap: 1,
                      textAlign: 'center',
                      p: 2
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 40, opacity: 0.4 }} />
                    <Typography variant="caption" sx={{ maxWidth: 200 }}>
                      Upload an image or enter a URL to see preview
                    </Typography>
                  </Box>
                )}
              </Paper>
              {/* Color Preview */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                  Color Preview
                </Typography>
                <Paper 
                  elevation={0}
                  sx={{ 
                    height: 50,
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative'
                  }}
                >
                  <Box sx={{ 
                    flex: 1, 
                    bgcolor: formData.primaryColor,
                    transition: 'all 0.3s ease'
                  }} />
                  <Box sx={{
                    position: 'absolute',
                    bottom: 4,
                    left: 0,
                    right: 0,
                    textAlign: 'center'
                  }}>
                    <Typography variant="caption" sx={{ 
                      color: theme => isLightColor(formData.primaryColor) ? '#000' : '#fff',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      textShadow: isLightColor(formData.primaryColor) ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {formData.primaryColor.toUpperCase()}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          bgcolor: theme => alpha(theme.palette.background.default, 0.5),
          gap: 1
        }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={isSaving || isUploading}
            variant="outlined"
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSaving || isUploading}
            startIcon={isSaving ? undefined : <CheckCircleOutlineIcon />}
            sx={{ 
              borderRadius: 1.5,
              px: 3,
              position: 'relative'
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress 
                  size={24} 
                  sx={{ 
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    marginLeft: '-12px',
                    marginTop: '-12px' 
                  }} 
                />
                <span style={{ visibility: 'hidden' }}>
                  {editChain ? 'Update' : 'Add'}
                </span>
              </>
            ) : (
              editChain ? 'Update Chain' : 'Add Chain'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: 3,
            borderRadius: 2
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Helper function to determine if a color is light or dark
const isLightColor = (hexColor) => {
  // Remove the # if it exists
  hexColor = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  
  // Calculate perceived brightness using the formula
  // (0.299*R + 0.587*G + 0.114*B)
  const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
  
  // Return true if the color is light (brightness > 150)
  return brightness > 150;
};

export default ChainManager; 